import { useState } from "react";
import styled from "styled-components";
import css from "@styled-system/css";
import { ColorProps, FlexDirectionProps, flexDirection } from "styled-system";

import Box from "../Box/Box";
import Typography from "../Typography/Typography";
import LazyImage from "../Image/LazyImage";
import ResponsiveLayout from "../ResponsiveLayout/ResponsiveLayout";
import { AnalyticsProps } from "../analytics.types";
import { VerticalSpaceProps } from "../types";

export interface FeaturedProductProps {
	id: string;
	title: string;
	description: string;
	imageUrl: string;
	analytics?: AnalyticsProps;
}

type ImageObjectFitOptions =
	| "fill"
	| "contain"
	| "cover"
	| "none"
	| " scale-down";
export interface FeaturedProductPanelProps extends VerticalSpaceProps {
	/**
  - Represents the content of the featured product panel. Accepts an array of FeaturedProductProps. FeaturedProductProps contains the following props:
  - id: string;
  - title: string;
  - description: string;
  - imageUrl: string;
   */
	products: FeaturedProductProps[];
	/**
  The panel's background color. It accepts either a hex color code or a pattern library color alias.
  */
	backgroundColor?: string;
	/**Product description color */
	descriptionColor?: ColorProps["color"];
	imageObjectFit?: ImageObjectFitOptions;
}

const ImageContainer = styled(Box)`
	position: relative;
`;

export const FeaturedProducts = styled.ul<
	{
		descriptionColor?: ColorProps["color"];
	} & FlexDirectionProps
>`
	list-style-type: none;
	${css({
		display: "flex",
		flexDirection: ["row", "column"],
		justifyContent: "center",
		pr: "100px",
		py: 0,
		"& li:last-child": {
			mb: 0,
		},
		pl: 0,
	})};
	${flexDirection}
`;

export const FeaturedProduct = styled.li`
	${css({
		py: 4,
		px: 5,
		mb: 6,
		minWidth: "312px",
		width: "312px",
		boxSizing: "content-box",
		cursor: "pointer",
		position: "relative",
		opacity: 0.3,
	})}
	&.active {
		opacity: 1;
		transition: opacity 0.5s ease-out;
		&.featured.active::before {
			content: "";
			height: 100%;
			position: absolute;
			left: -4px;
			top: 0px;
			bottom: 0px;
			border-left: 3px solid;
			${css({
				borderColor: "secondary-orange",
			})};
		}
		&.featured.forward::before {
			animation: moveForward 0.2s;
			@keyframes moveForward {
				0% {
					transform: translateY(-100%);
				}
				100% {
					transform: translateY(0%);
				}
			}
		}
		&.backward::before {
			animation: moveBackward 0.2s;
			@keyframes moveBackward {
				0% {
					transform: translateY(100%);
				}
				100% {
					transform: translateY(0%);
				}
			}
		}
	}
`;

export const ProductImage = ({
	products,
	activeProductIndex,
	containerHeight = "auto",
	imageObjectFit = "cover",
}: {
	products: FeaturedProductProps[];
	activeProductIndex: number;
	containerHeight?: number | string | (string | number)[];
	imageObjectFit?: ImageObjectFitOptions;
}) => {
	return (
		<>
			<ImageContainer width="100%" height={containerHeight}>
				{products.map((product, index) => (
					<LazyImage
						id={product.id}
						src={product.imageUrl}
						alt={product.title}
						width="100%"
						height="100%"
						className={index === activeProductIndex ? "active" : ""}
						key={product.id}
						css={`
							position: absolute;
							left: 0;
							-webkit-transition: opacity 0.4s ease-in-out;
							-moz-transition: opacity 0.4s ease-in-out;
							-o-transition: opacity 0.4s ease-in-out;
							transition: opacity 0.4s ease-in-out;
							opacity: 0;
							> img {
								object-fit: ${imageObjectFit};
							}

							&.active {
								opacity: 1;
							}
						`}
					/>
				))}
			</ImageContainer>
		</>
	);
};

const ProductsPanel = ({
	products,
	descriptionColor,
	imageObjectFit,
}: {
	products: FeaturedProductProps[];
	descriptionColor: ColorProps["color"];
	imageObjectFit?: ImageObjectFitOptions;
}) => {
	const [activeIndex, setActive] = useState(0);
	const [prevIndex, setPrevActive] = useState(0);

	return (
		<Box
			display={{ _: "none", desktop: "flex" }}
			flexDirection="row"
			width="100%"
		>
			<FeaturedProducts flexDirection={{ _: "row", desktop: "column" }}>
				{products.map((product, index) => {
					const eventTitle =
						product.title && product.title.length > 250
							? product.title.substr(0, 250)
							: product.title;

					return (
						<FeaturedProduct
							key={product.id}
							className={
								activeIndex === index
									? prevIndex < activeIndex
										? "featured active forward"
										: "featured active backward"
									: ""
							}
							onClick={() => {
								setActive((prevIndex) => {
									setPrevActive(prevIndex);
									return index;
								});
							}}
							data-en="click"
							data-ec="panel"
							data-el={eventTitle || ""}
							data-ea={product.analytics?.eventAction}
							data-es={product.analytics?.eventSection}
							data-et={product.analytics?.eventType}
							data-ev={product.analytics?.eventValue}
							data-testid="container-item"
						>
							<Typography
								variant="h5"
								color="primary-white"
								m={0}
								mb={3}
								css={`
									line-height: 1.2;
								`}
							>
								{product.title}
							</Typography>
							<Typography variant="body" color={descriptionColor} m={0}>
								{product.description}
							</Typography>
						</FeaturedProduct>
					);
				})}
			</FeaturedProducts>
			<ProductImage
				products={products}
				activeProductIndex={activeIndex}
				imageObjectFit={imageObjectFit}
			/>
		</Box>
	);
};

const ProductsPanelMobile = ({
	products,
	descriptionColor,
}: {
	products: FeaturedProductProps[];
	descriptionColor: ColorProps["color"];
}) => {
	return (
		<Box
			display={{ _: "flex", desktop: "none" }}
			flexDirection="column"
			width="100%"
		>
			{products.map((product, index) => (
				<Box key={product.id} mb={index === products.length - 1 ? 0 : "60px"}>
					<Typography
						variant="h5"
						color="primary-white"
						m={0}
						mb={3}
						css={`
							line-height: 1.2;
						`}
					>
						{product.title}
					</Typography>
					<Typography variant="body" color={descriptionColor} m={0} mb="40px">
						{product.description}
					</Typography>
					<LazyImage
						src={product.imageUrl}
						alt={product.title}
						width="100%"
						height="auto"
						key={product.id}
					></LazyImage>
				</Box>
			))}
		</Box>
	);
};

export default function FeaturedProductPanel({
	products,
	backgroundColor = "secondary-nav-blue",
	paddingY = { _: "44px", desktop: "60px" },
	descriptionColor = "cta-primary-text-color-default",
	imageObjectFit,
	...props
}: FeaturedProductPanelProps) {
	return (
		<Box backgroundColor={backgroundColor} m={0}>
			<ResponsiveLayout paddingY={paddingY} {...props}>
				<ProductsPanel
					products={products}
					descriptionColor={descriptionColor}
					imageObjectFit={imageObjectFit}
				/>
				<ProductsPanelMobile
					products={products}
					descriptionColor={descriptionColor}
				/>
			</ResponsiveLayout>
		</Box>
	);
}
